/**
 * @name MenuExport
 * @description Componente para gestionar las opciones de exportar del reporte
 * @author Ricardo Guerra
 */

import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import styled from 'styled-components';

import { CSVLink } from 'react-csv';

// Material-UI
import MuiList from '@material-ui/core/List';
import MuiListItem from '@material-ui/core/ListItem';
import MuiCircularProgress from '@material-ui/core/CircularProgress';

import { Flex, MUI, Unicons, Theme, Typography } from '@styles';

const MenuExport = (props) => {
  const {
    columns,
    rows,
    exportToCSV,
    exportAllToExcel,
    exportFiltersToExcel,
    exportFileName,
    onExportAllToExcel,
    onExportFiltersToExcel,
  } = props;

  const [anchorMenu, setAnchorMenu] = useState(null);
  const [loadingAction, setLoadingAction] = useState(null);

  const hasRows = Array.isArray(rows) && rows.length > 0;
  const isBusy = loadingAction !== null;

  const headers = useMemo(() => {
    if (!exportToCSV) {
      return [];
    }
    return columns.map(({ name, header }) => ({ label: header, key: name }));
  }, [columns, exportToCSV]);

  const openMenu = (event) => {
    if (!hasRows || isBusy) {
      return;
    }
    setAnchorMenu(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorMenu(null);
  };

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const withLoading = async (action, callback) => {
    if (!hasRows || isBusy) {
      return;
    }

    setLoadingAction(action);
    try {
      await Promise.resolve(callback && callback());
    } finally {
      setLoadingAction(null);
      closeMenu();
    }
  };

  return (
    <>
      <MUI.Tooltip title={hasRows ? i18n.t('Report.export') : i18n.t('GenericMessages.noResults')}>
        <span>
          <MUI.ButtonIcon onClick={openMenu} disabled={!hasRows || isBusy}>
            <Unicons.Export size="20" />
          </MUI.ButtonIcon>
        </span>
      </MUI.Tooltip>

      <Popover
        open={Boolean(anchorMenu)}
        anchorEl={anchorMenu}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Flex.Col padding="16px" style={{ borderBottom: `1px solid ${Theme.colors.greyLight}` }}>
          <Typography.H6>{i18n.t('Report.export')}</Typography.H6>
          <Typography.Text>{i18n.t('Report.selectExportOptions')}</Typography.Text>
        </Flex.Col>

        {(exportToCSV || exportAllToExcel || exportFiltersToExcel) && (
          <List component="nav" aria-label="export-list">
            {/* Export CSV */}
            {exportToCSV && (
              <StyledCSVLink
                headers={headers}
                data={hasRows ? rows : []}
                filename={`${exportFileName}.csv`}
                onClick={() => withLoading('csv', () => delay(900))}
              >
                <ListItem button disabled={!hasRows || isBusy}>
                  <Styled.RowBetween>
                    <Typography.Text>{i18n.t('Report.exportToCsv')}</Typography.Text>
                    {loadingAction === 'csv' && <Styled.CircularProgress size={20} thickness={4} />}
                  </Styled.RowBetween>
                </ListItem>
              </StyledCSVLink>
            )}
            {/* Export All Excel */}
            {exportAllToExcel && (
              <ListItem
                button
                disabled={!hasRows || isBusy}
                onClick={() => withLoading('allExcel', onExportAllToExcel)}
              >
                <Styled.RowBetween>
                  <Typography.Text>{i18n.t('Report.exportAllToExcel')}</Typography.Text>
                  {loadingAction === 'allExcel' && <Styled.CircularProgress size={20} thickness={4} />}
                </Styled.RowBetween>
              </ListItem>
            )}
            {/* Export CSV */}
            {exportFiltersToExcel && (
              <ListItem
                button
                disabled={!hasRows || isBusy}
                onClick={() => withLoading('filtersExcel', onExportFiltersToExcel)}
              >
                <Styled.RowBetween>
                  <Typography.Text>{i18n.t('Report.exportFiltetredToExcel')}</Typography.Text>
                  {loadingAction === 'filtersExcel' && <Styled.CircularProgress size={20} thickness={4} />}
                </Styled.RowBetween>
              </ListItem>
            )}
          </List>
        )}
      </Popover>
    </>
  );
};

MenuExport.propTypes = {
  // Colección de columnas a mostrar en el grid. 
  columns: PropTypes.array.isRequired,
  // Registros mostrados actualmente en el grid.
  rows: PropTypes.array.isRequired,
  // Boolean que muestra la opción de exportar a csv.
  exportToCSV: PropTypes.bool,
  // Boolean que muestra la opción de exportar a todos los registros a Excel.
  exportAllToExcel: PropTypes.bool,
  // Boolean que muestra la opción de exportar a los registros filtrados a Excel.
  exportFiltersToExcel: PropTypes.bool,
  // Nombre que se le da al archivo cuando se exportan los registros.
  exportFileName: PropTypes.string,
  // Función para exportar a todos los registros a Excel.
  onExportAllToExcel: PropTypes.func,
  // Función para exportar a los registros filtrados a Excel.
  onExportFiltersToExcel: PropTypes.func
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

const Popover = styled(MUI.Popover)`
  .MuiPopover-paper {
    border-radius: 6px;
    box-shadow: ${(props) => props.theme.shadows.depth4};
  }
`;

const List = styled(MuiList)`
  &.MuiList-padding {
    padding-top: 8px;
    padding-bottom: 8px;
    max-height: 450px;
    overflow-y: auto;
  }
`;

const ListItem = styled(MuiListItem)`
  &.MuiListItem-root {
    min-height: 40px;
    padding: 0px 16px;
  }

  & > a {
    text-decoration: none;
  }
`;

const StyledCSVLink = styled(CSVLink)`
  text-decoration: none;
`;

Styled.RowBetween = styled(Flex.Row)`
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

// Circular Progress
Styled.CircularProgress = styled(MuiCircularProgress)`
  &.MuiCircularProgress-root {
    color: ${(props) => props.theme.colors.blueMedium};
  }
`;

export default MenuExport;